/**
 * ReportBuilderPage — Admin page to configure which report form fields
 * are visible and required per EmploymentRole + ReportType combination.
 * Drives both the frontend QuickReportModal and backend validation.
 */

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SlidersHorizontal,
  Lock,
  Eye,
  EyeOff,
  Asterisk,
  RotateCcw,
  Save,
  Loader2,
  ChevronDown,
  Building2Icon,
  CalendarIcon,
  FileTextIcon,
  HardHatIcon,
  HashIcon,
  ClockIcon,
  StickyNoteIcon,
  Star,
} from 'lucide-react';
import { motion } from 'motion/react';
import toast from 'react-hot-toast';
import {
  ADMIN_COLORS,
} from '../utils/adminAnimations';
import {
  FIELD_CATALOG,
  getDefaultFieldOverrides,
  parseFieldOverrides,
  type FieldOverrides,
} from '../utils/reportFieldCatalog';
import {
  useActiveEmploymentRoles,
  useEmploymentRoleRates,
  useUpdateRate,
  useUpdateEmploymentRole,
  useEmploymentRoleDetail,
} from '../hooks';
import { useUpdateReportType } from '@/features/reports/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { MODAL_COLORS } from '@/components/modal';
import { localizedName } from '@/utils/localized';
import { getQuantityLabel } from '@/utils/quantityLabel';

/** Map field keys to lucide icons (rendered inline to avoid style prop issues) */
const FIELD_ICONS: Record<string, React.ReactNode> = {
  hospital: <Building2Icon className="w-4 h-4 flex-shrink-0" />,
  date: <CalendarIcon className="w-4 h-4 flex-shrink-0" />,
  treatmentType: <FileTextIcon className="w-4 h-4 flex-shrink-0" />,
  helmetType: <HardHatIcon className="w-4 h-4 flex-shrink-0" />,
  quantity: <HashIcon className="w-4 h-4 flex-shrink-0" />,
  startTime: <ClockIcon className="w-4 h-4 flex-shrink-0" />,
  endTime: <ClockIcon className="w-4 h-4 flex-shrink-0" />,
  notes: <StickyNoteIcon className="w-4 h-4 flex-shrink-0" />,
};

const PREVIEW_ICONS: Record<string, React.ReactNode> = {
  hospital: <Building2Icon className="w-3.5 h-3.5 opacity-60" />,
  date: <CalendarIcon className="w-3.5 h-3.5 opacity-60" />,
  treatmentType: <FileTextIcon className="w-3.5 h-3.5 opacity-60" />,
  helmetType: <HardHatIcon className="w-3.5 h-3.5 opacity-60" />,
  quantity: <HashIcon className="w-3.5 h-3.5 opacity-60" />,
  startTime: <ClockIcon className="w-3.5 h-3.5 opacity-60" />,
  endTime: <ClockIcon className="w-3.5 h-3.5 opacity-60" />,
  notes: <StickyNoteIcon className="w-3.5 h-3.5 opacity-60" />,
};

export function ReportBuilderPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const queryClient = useQueryClient();

  // ─── Data ─────────────────────────────────────────────
  const { data: roles, isLoading: rolesLoading } = useActiveEmploymentRoles();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const { data: rates, isLoading: rtsLoading } = useEmploymentRoleRates(selectedRoleId || undefined);
  const { data: roleDetail } = useEmploymentRoleDetail(selectedRoleId || undefined);
  const [selectedRateId, setSelectedRateId] = useState<string>('');
  const updateMutation = useUpdateRate();
  const updateRoleMutation = useUpdateEmploymentRole();
  const updateReportTypeMutation = useUpdateReportType();

  // ─── Local config state ───────────────────────────────
  const [overrides, setOverrides] = useState<FieldOverrides>(getDefaultFieldOverrides());
  const [savedSnapshot, setSavedSnapshot] = useState<string>('');

  // ─── Quantity alias state ─────────────────────────────
  const [quantityAlias, setQuantityAlias] = useState('');
  const [quantityAliasEn, setQuantityAliasEn] = useState('');
  const [savedAliasSnapshot, setSavedAliasSnapshot] = useState('');
  const isAliasDirty = JSON.stringify({ quantityAlias, quantityAliasEn }) !== savedAliasSnapshot;

  const overridesDirty = JSON.stringify(overrides) !== savedSnapshot;
  const isDirty = overridesDirty || isAliasDirty;

  // Auto-select first role when data loads
  useEffect(() => {
    if (roles?.length && !selectedRoleId) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  // Auto-select first report type when role changes
  useEffect(() => {
    if (rates?.length) {
      setSelectedRateId(rates[0].id);
    } else {
      setSelectedRateId('');
    }
  }, [rates]);

  // Load field overrides when selected ERT changes
  const selectedRate = useMemo(
    () => rates?.find((rt: any) => rt.id === selectedRateId),
    [rates, selectedRateId],
  );

  useEffect(() => {
    if (selectedRate) {
      const parsed = parseFieldOverrides(selectedRate.fieldOverrides);
      setOverrides(parsed);
      setSavedSnapshot(JSON.stringify(parsed));
      const rt = selectedRate.reportType;
      setQuantityAlias(rt?.quantityAlias ?? '');
      setQuantityAliasEn(rt?.quantityAliasEn ?? '');
      setSavedAliasSnapshot(JSON.stringify({ quantityAlias: rt?.quantityAlias ?? '', quantityAliasEn: rt?.quantityAliasEn ?? '' }));
    }
  }, [selectedRate]);

  // ─── Handlers ─────────────────────────────────────────
  const toggleVisible = (key: string) => {
    setOverrides((prev) => {
      const field = prev[key];
      if (!field) return prev;
      const newVisible = !field.visible;
      return {
        ...prev,
        [key]: {
          visible: newVisible,
          required: newVisible ? field.required : false,
        },
      };
    });
  };

  const toggleRequired = (key: string) => {
    setOverrides((prev) => {
      const field = prev[key];
      if (!field || !field.visible) return prev;
      return {
        ...prev,
        [key]: { ...field, required: !field.required },
      };
    });
  };

  const resetDefaults = () => {
    setOverrides(getDefaultFieldOverrides());
  };

  const handleSetDefault = async (reportTypeCode: string) => {
    if (!selectedRoleId) return;
    try {
      await updateRoleMutation.mutateAsync({
        id: selectedRoleId,
        data: { defaultReportTypeCode: reportTypeCode },
      });
      toast.success(t('reportBuilder.defaultSaved'));
    } catch {
      toast.error(t('reportBuilder.saveFailed'));
    }
  };

  const handleSave = async () => {
    if (!selectedRoleId || !selectedRateId) return;
    try {
      if (overridesDirty) {
        await updateMutation.mutateAsync({
          roleId: selectedRoleId,
          rateId: selectedRateId,
          data: { fieldOverrides: JSON.stringify(overrides) },
        });
        setSavedSnapshot(JSON.stringify(overrides));
      }
      if (isAliasDirty && selectedRate?.reportType?.id) {
        await updateReportTypeMutation.mutateAsync({
          id: selectedRate.reportType.id,
          data: { quantityAlias: quantityAlias.trim() || undefined, quantityAliasEn: quantityAliasEn.trim() || undefined },
        });
        setSavedAliasSnapshot(JSON.stringify({ quantityAlias, quantityAliasEn }));
        // Refresh employment-role rates so the embedded reportType data picks up new aliases
        queryClient.refetchQueries({ queryKey: ['employment-roles'] });
      }
      toast.success(t('reportBuilder.saved'));
    } catch {
      toast.error(t('reportBuilder.saveFailed'));
    }
  };

  // ─── Render ───────────────────────────────────────────
  return (
    <div className="w-full max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, var(--color-primary-500), var(--color-accent-500))' }}
        >
          <SlidersHorizontal className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold" style={{ color: ADMIN_COLORS.navy }}>
            {t('reportBuilder.title')}
          </h1>
          <p className="text-sm" style={{ color: ADMIN_COLORS.textSecondary }}>
            {t('reportBuilder.subtitle')}
          </p>
        </div>
      </div>

      {/* Role Selector */}
      <div
        className="rounded-xl p-4"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <label className="text-sm font-semibold mb-2 block" style={{ color: ADMIN_COLORS.textSecondary }}>
          {t('reportBuilder.selectRole')}
        </label>
        {rolesLoading ? (
          <div className="flex items-center gap-2 py-2">
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: ADMIN_COLORS.primary }} />
          </div>
        ) : !roles?.length ? (
          <p className="text-sm py-2" style={{ color: ADMIN_COLORS.textSecondary }}>
            {t('reportBuilder.noRoles')}
          </p>
        ) : (
          <div className="relative">
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full p-2.5 rounded-lg text-base font-semibold appearance-none cursor-pointer focus:outline-none focus:ring-2"
              style={{
                backgroundColor: ADMIN_COLORS.surface,
                border: `1px solid ${ADMIN_COLORS.border}`,
                color: ADMIN_COLORS.navy,
              }}
            >
              {roles.map((role: any) => (
                <option key={role.id} value={role.id}>
                  {localizedName(role, lang)}
                </option>
              ))}
            </select>
            <ChevronDown
              className="absolute top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4"
              style={{ right: '12px', color: ADMIN_COLORS.textSecondary }}
            />
          </div>
        )}
      </div>

      {/* Report Type Tabs */}
      {selectedRoleId && (
        <div>
          {rtsLoading ? (
            <div className="flex items-center gap-2 py-3">
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: ADMIN_COLORS.primary }} />
            </div>
          ) : !rates?.length ? (
            <p
              className="text-sm py-3 px-4 rounded-xl"
              style={{
                color: ADMIN_COLORS.textSecondary,
                backgroundColor: ADMIN_COLORS.surface,
                border: `1px solid ${ADMIN_COLORS.border}`,
              }}
            >
              {t('reportBuilder.noReportTypes')}
            </p>
          ) : (
            <>
              <div className="flex gap-2 flex-wrap items-center">
                {rates.map((ert: any) => {
                  const isSelected = selectedRateId === ert.id;
                  const isDefault = roleDetail?.defaultReportTypeCode === ert.reportType.code;
                  return (
                    <button
                      key={ert.id}
                      type="button"
                      onClick={() => setSelectedRateId(ert.id)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer"
                      style={{
                        backgroundColor: isSelected ? ADMIN_COLORS.primary : ADMIN_COLORS.surface,
                        color: isSelected ? '#fff' : ADMIN_COLORS.navy,
                        border: `1px solid ${isSelected ? ADMIN_COLORS.primary : ADMIN_COLORS.border}`,
                        boxShadow: isSelected ? '0 2px 8px color-mix(in srgb, var(--color-primary-500) 30%, transparent)' : ADMIN_COLORS.shadow,
                      }}
                    >
                      {isDefault && (
                        <Star className="w-3.5 h-3.5 fill-current" />
                      )}
                      {localizedName(ert.reportType, lang)}
                    </button>
                  );
                })}
              </div>

              {/* Set as Default for selected report type */}
              {selectedRate && roleDetail && (
                <div className="mt-2">
                  {roleDetail.defaultReportTypeCode === selectedRate.reportType.code ? (
                    <span
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
                      style={{ color: ADMIN_COLORS.primary, backgroundColor: ADMIN_COLORS.primarySoft }}
                    >
                      <Star className="w-3.5 h-3.5 fill-current" />
                      {t('reportBuilder.defaultType')}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSetDefault(selectedRate.reportType.code)}
                      disabled={updateRoleMutation.isPending}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                      style={{
                        color: ADMIN_COLORS.textSecondary,
                        backgroundColor: 'rgba(148, 163, 184, 0.1)',
                      }}
                    >
                      <Star className="w-3.5 h-3.5" />
                      {t('reportBuilder.setAsDefault')}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Config + Preview panels */}
      {selectedRate && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Config Panel */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl p-4 space-y-3"
            style={{
              backgroundColor: ADMIN_COLORS.surface,
              border: `1px solid ${ADMIN_COLORS.border}`,
              boxShadow: ADMIN_COLORS.shadow,
            }}
          >
            <h3 className="text-sm font-bold" style={{ color: ADMIN_COLORS.navy }}>
              {t('reportBuilder.configPanel')}
            </h3>

            <div className="space-y-1">
              {/* Column headers */}
              <div className="flex items-center gap-3 px-3 py-1">
                <div className="flex-1" />
                <span className="w-16 text-center text-xs font-semibold" style={{ color: ADMIN_COLORS.textSecondary }}>
                  {t('reportBuilder.fieldVisible')}
                </span>
                <span className="w-16 text-center text-xs font-semibold" style={{ color: ADMIN_COLORS.textSecondary }}>
                  {t('reportBuilder.fieldRequired')}
                </span>
              </div>

              {FIELD_CATALOG.map((field) => {
                const cfg = overrides[field.key];
                if (!cfg) return null;
                const isLocked = field.locked;

                return (
                  <div key={field.key}>
                    <div
                      className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors"
                      style={{
                        backgroundColor: cfg.visible ? 'color-mix(in srgb, var(--color-primary-500) 4%, transparent)' : 'transparent',
                      }}
                    >
                      {/* Label */}
                      <div
                        className="flex items-center gap-2 flex-1 min-w-0"
                        style={{ color: cfg.visible ? ADMIN_COLORS.primary : ADMIN_COLORS.textSecondary }}
                      >
                        {isLocked && (
                          <Lock className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ADMIN_COLORS.textSecondary }} />
                        )}
                        {FIELD_ICONS[field.key]}
                        <span
                          className="text-sm font-medium truncate"
                          style={{ color: cfg.visible ? ADMIN_COLORS.navy : ADMIN_COLORS.textSecondary }}
                        >
                          {t(field.labelKey)}
                        </span>
                      </div>

                      {/* Visible toggle */}
                      <div className="w-16 flex justify-center">
                        <button
                          type="button"
                          disabled={isLocked}
                          onClick={() => toggleVisible(field.key)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          style={{
                            backgroundColor: cfg.visible ? ADMIN_COLORS.primarySoft : 'rgba(148, 163, 184, 0.1)',
                          }}
                        >
                          {cfg.visible ? (
                            <Eye className="w-4 h-4" style={{ color: ADMIN_COLORS.primary }} />
                          ) : (
                            <EyeOff className="w-4 h-4" style={{ color: ADMIN_COLORS.textSecondary }} />
                          )}
                        </button>
                      </div>

                      {/* Required toggle */}
                      <div className="w-16 flex justify-center">
                        <button
                          type="button"
                          disabled={isLocked || !cfg.visible}
                          onClick={() => toggleRequired(field.key)}
                          className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          style={{
                            backgroundColor: cfg.required ? 'rgba(239, 68, 68, 0.12)' : 'rgba(148, 163, 184, 0.1)',
                          }}
                        >
                          <Asterisk
                            className="w-4 h-4"
                            style={{ color: cfg.required ? '#ef4444' : ADMIN_COLORS.textSecondary }}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Quantity alias inputs */}
                    {field.key === 'quantity' && cfg.visible && (
                      <div className="grid grid-cols-2 gap-2 px-3 py-2 ms-6">
                        <div className="space-y-0.5">
                          <label className="text-[11px] font-semibold" style={{ color: ADMIN_COLORS.textSecondary }}>
                            {t('reportBuilder.quantityAliasHe')}
                          </label>
                          <input
                            type="text"
                            value={quantityAlias}
                            onChange={(e) => setQuantityAlias(e.target.value)}
                            placeholder={t('reportBuilder.fields.quantity')}
                            className="w-full px-2 py-1.5 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
                            style={{
                              backgroundColor: ADMIN_COLORS.surface,
                              border: `1px solid ${ADMIN_COLORS.border}`,
                              color: ADMIN_COLORS.navy,
                            }}
                          />
                        </div>
                        <div className="space-y-0.5">
                          <label className="text-[11px] font-semibold" style={{ color: ADMIN_COLORS.textSecondary }}>
                            {t('reportBuilder.quantityAliasEn')}
                          </label>
                          <input
                            type="text"
                            value={quantityAliasEn}
                            onChange={(e) => setQuantityAliasEn(e.target.value)}
                            placeholder="Quantity"
                            className="w-full px-2 py-1.5 rounded-lg text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
                            style={{
                              backgroundColor: ADMIN_COLORS.surface,
                              border: `1px solid ${ADMIN_COLORS.border}`,
                              color: ADMIN_COLORS.navy,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={resetDefaults}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
                style={{
                  backgroundColor: 'rgba(148, 163, 184, 0.1)',
                  color: ADMIN_COLORS.textSecondary,
                }}
              >
                <RotateCcw className="w-3.5 h-3.5" />
                {t('reportBuilder.resetDefaults')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || updateMutation.isPending}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: isDirty ? ADMIN_COLORS.primary : 'rgba(148, 163, 184, 0.15)',
                  color: isDirty ? '#fff' : ADMIN_COLORS.textSecondary,
                  boxShadow: isDirty ? '0 2px 8px color-mix(in srgb, var(--color-primary-500) 30%, transparent)' : 'none',
                }}
              >
                {updateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {updateMutation.isPending ? t('reportBuilder.saving') : t('reportBuilder.saveConfig')}
              </button>
            </div>
          </motion.div>

          {/* Preview Panel */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="rounded-xl p-4 space-y-3"
            style={{
              backgroundColor: ADMIN_COLORS.surface,
              border: `1px solid ${ADMIN_COLORS.border}`,
              boxShadow: ADMIN_COLORS.shadow,
            }}
          >
            <h3 className="text-sm font-bold" style={{ color: ADMIN_COLORS.navy }}>
              {t('reportBuilder.previewPanel')}
            </h3>

            {/* Mock form card */}
            <div
              className="rounded-xl p-4 space-y-3"
              style={{
                backgroundColor: MODAL_COLORS.surface,
                border: `1px solid ${MODAL_COLORS.border}`,
              }}
            >
              {FIELD_CATALOG.filter((f) => overrides[f.key]?.visible).map((field) => {
                const cfg = overrides[field.key];
                const previewLabel = field.key === 'quantity'
                  ? getQuantityLabel(selectedRate?.reportType, lang, t(field.labelKey))
                  : t(field.labelKey);

                return (
                  <div key={field.key} className="space-y-1">
                    <div className="flex items-center gap-1.5" style={{ color: MODAL_COLORS.text }}>
                      {PREVIEW_ICONS[field.key]}
                      <span className="text-xs font-semibold" style={{ color: MODAL_COLORS.text }}>
                        {previewLabel}
                        {cfg?.required && (
                          <span style={{ color: '#ef4444' }}> *</span>
                        )}
                      </span>
                    </div>
                    <div
                      className="w-full h-9 rounded-lg"
                      style={{
                        backgroundColor: MODAL_COLORS.interactive,
                        border: `1px solid ${MODAL_COLORS.border}`,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
